import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShieldAlert, DollarSign, Lock, Gavel, Link2, Folder, ChevronRight, ChevronLeft, Sparkles, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import LSSDLogo from "../components/LSSDLogo";
import PenalCodeChatbot from "../components/PenalCodeChatbot";

const SEVERITY = {
  "INFRAZIONE": { chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", dot: "bg-emerald-400", label: "INFRAZIONE", order: 1 },
  "REATO MINORE": { chip: "bg-amber-500/15 text-amber-300 border-amber-500/40", dot: "bg-amber-400", label: "REATO MINORE", order: 2 },
  "REATO MAGGIORE": { chip: "bg-rose-600/20 text-rose-300 border-rose-600/50", dot: "bg-rose-400", label: "REATO MAGGIORE", order: 3 },
};

const CATEGORY_ORDER = [
  "EMENDAMENTI",
  "DISPOSIZIONI",
  "REATI CONTRO LA PERSONA",
  "CRIMINI CONTRO LA PROPRIETÀ",
  "REATI CONTRO ORDINE PUBBLICO",
  "REATI CONTRO LA PUBBLICA AMMINISTRAZIONE",
  "REATI CONTRO L'AMMINISTRAZIONE DELLA GIUSTIZIA",
  "REATI LEGATI ALLE ARMI",
  "REATI LEGATI ALLE SOSTANZE STUPEFACENTI",
  "REATI FINANZIARI ED ECONOMICI",
  "REATI CONTRO LA SICUREZZA PUBBLICA",
  "REATI CONTRO LA FAMIGLIA E I MINORI",
  "CODICE DELLA STRADA",
];

export default function CodicePenalePublic() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null); // null = pick category, else display
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/penal-code")
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data);
        // Deep link ?art=187 applicato una sola volta al primo caricamento
        if (!deepLinkAppliedRef.current) {
          const art = params.get("art");
          if (art) {
            const found = data.find((p) => p.code === art);
            if (found) {
              setCat(found.category);
              setActive(found);
            }
          }
          deepLinkAppliedRef.current = true;
        }
      })
      .catch(() => { if (!cancelled) toast.error("Impossibile caricare il codice penale"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- params letti una sola volta al mount
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((p) => {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category).push(p);
    });
    return map;
  }, [items]);

  const orderedCategories = useMemo(() => {
    const cats = Array.from(grouped.keys());
    return cats.sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [grouped]);

  // Search across all items returns list of matching cards
  const searchResults = useMemo(() => {
    if (!q.trim()) return null;
    return items.filter((p) =>
      [p.code, p.title, p.category, p.description].some((s) => (s || "").toLowerCase().includes(q.toLowerCase()))
    );
  }, [q, items]);

  const copyArticleLink = (code) => {
    const url = `${window.location.origin}/codice-penale?art=${encodeURIComponent(code)}`;
    navigator.clipboard?.writeText(url).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    });
    toast.success("Link Copiato!", { duration: 2500 });
  };

  if (loading) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-sheriff-gold" /></div>;
  }

  return (
    <div className="relative bg-[#0A1F18] min-h-screen">
      {/* Background pattern: radiale oro tenue + grid */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-sheriff-gold/5 blur-3xl" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-sheriff-green-light/10 blur-3xl" />
      </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="public-penal-code-page">
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-sheriff-gold/10 border border-sheriff-gold/30 rounded-full">
            <ShieldAlert className="h-3.5 w-3.5 text-sheriff-gold" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-sheriff-gold">Sistema Legislativo Ufficiale</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#F5E6B8] leading-[0.95]">
            Codice Penale<br /><span className="text-sheriff-gold">San Andreas</span>
          </h1>
          <p className="text-[#D4C480] text-base sm:text-lg max-w-2xl leading-relaxed mt-4">
            Naviga per categoria oppure cerca direttamente per codice, reato o testo.
          </p>
        </div>
        <div className="hidden lg:block">
          <LSSDLogo className="h-32 w-32 opacity-90" />
        </div>
      </div>

      {/* SEARCH */}
      <div className="relative mb-8">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-sheriff-gold/50" />
        <Input
          data-testid="public-penal-search"
          placeholder="Cerca per codice, reato, descrizione…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-14 pr-5 h-14 bg-[#143830] border-2 border-sheriff-gold/40 text-[#F5E6B8] placeholder:text-[#8B7B49] rounded-2xl text-base focus-visible:ring-sheriff-gold/40 focus-visible:border-sheriff-gold/60"
        />
      </div>

      {/* CONTENT: 3 modalità */}
      {searchResults ? (
        // Modalità ricerca
        <SearchResults results={searchResults} onPick={setActive} q={q} />
      ) : cat === null ? (
        // Modalità griglia gruppi (categorie)
        <CategoryGrid categories={orderedCategories} grouped={grouped} onPick={setCat} />
      ) : (
        // Modalità dettaglio categoria → sottogruppi per gravità
        <CategoryDetail category={cat} items={grouped.get(cat) || []} onBack={() => { setCat(null); setParams({}); }} onPick={setActive} />
      )}

      {/* DIALOG */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); if (params.get("art")) setParams({}); } }}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="penal-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full border ${(SEVERITY[active.severity] || SEVERITY.INFRAZIONE).chip}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${(SEVERITY[active.severity] || SEVERITY.INFRAZIONE).dot}`} />
                    {active.severity}
                  </span>
                  {active.process && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full border bg-purple-500/15 text-purple-300 border-purple-500/40">
                      <Gavel className="h-2.5 w-2.5" /> Rinviabile a Processo
                    </span>
                  )}
                  {active.bail && (
                    <span data-testid="penal-detail-bail-badge" className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                      Cauzione
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full bg-sheriff-gold/10 text-sheriff-gold/80">
                    <Folder className="h-2.5 w-2.5" /> {active.category}
                  </span>
                </div>
                <DialogTitle className="font-heading font-black text-2xl sm:text-3xl text-[#F5E6B8] leading-tight pr-8 text-left">
                  <span className="font-mono text-sheriff-gold mr-3">{active.code}</span>
                  {active.title}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 pt-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold mb-2">Descrizione</div>
                  <p className="text-[#E8D89A] leading-relaxed whitespace-pre-wrap text-sm">{active.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#0A1F18] border border-sheriff-gold/20 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/70 flex items-center gap-1.5 mb-2">
                      <Lock className="h-3 w-3" /> Detenzione
                    </div>
                    <div className="font-mono text-[#F5E6B8] font-bold text-lg">{active.jail}</div>
                  </div>
                  <div className="bg-[#0A1F18] border border-sheriff-gold/20 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/70 flex items-center gap-1.5 mb-2">
                      <DollarSign className="h-3 w-3" /> Multa
                    </div>
                    <div className="font-mono text-sheriff-gold font-bold text-lg">{active.fine}</div>
                  </div>
                </div>

                <button
                  onClick={() => copyArticleLink(active.code)}
                  data-testid="penal-copy-link"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-xl text-sm uppercase tracking-[0.15em] transition-colors"
                >
                  <Link2 className="h-4 w-4" /> Copia Link Articolo
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <PenalCodeChatbot />
    </div>
    </div>
  );
}

// ---------- COMPONENTS ----------

function CategoryGrid({ categories, grouped, onPick }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="penal-category-grid">
      {categories.map((c, idx) => {
        const arr = grouped.get(c) || [];
        const counts = {
          maj: arr.filter((p) => p.severity === "REATO MAGGIORE").length,
          min: arr.filter((p) => p.severity === "REATO MINORE").length,
          inf: arr.filter((p) => p.severity === "INFRAZIONE").length,
        };
        return (
          <button
            key={c}
            onClick={() => onPick(c)}
            data-testid={`penal-cat-card-${c}`}
            className="group text-left bg-gradient-to-br from-[#143830] to-[#0F2620] hover:from-[#1B4D3E] hover:to-[#143830] border-2 border-sheriff-gold/40 hover:border-sheriff-gold shadow-lg shadow-sheriff-gold/5 rounded-2xl p-6 transition-all duration-200 hover:shadow-xl hover:shadow-sheriff-gold/10 hover:-translate-y-0.5"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60">Capitolo {String(idx + 1).padStart(2, "0")}</div>
              <ChevronRight className="h-5 w-5 text-sheriff-gold/40 group-hover:text-sheriff-gold group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="font-heading font-bold text-xl text-[#F5E6B8] group-hover:text-sheriff-gold transition-colors mb-4 leading-tight">{c}</h3>
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <span className="text-sheriff-gold font-mono font-bold">{arr.length} articoli</span>
              {counts.maj > 0 && <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-rose-400" /><span className="text-rose-300/70">{counts.maj}</span></span>}
              {counts.min > 0 && <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /><span className="text-amber-300/70">{counts.min}</span></span>}
              {counts.inf > 0 && <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /><span className="text-emerald-300/70">{counts.inf}</span></span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function CategoryDetail({ category, items, onBack, onPick }) {
  // Raggruppa per severity (sottogruppi)
  const subgroups = useMemo(() => {
    const map = new Map();
    items.forEach((p) => {
      if (!map.has(p.severity)) map.set(p.severity, []);
      map.get(p.severity).push(p);
    });
    return Array.from(map.entries()).sort(([a], [b]) => (SEVERITY[b]?.order || 0) - (SEVERITY[a]?.order || 0));
  }, [items]);

  return (
    <div data-testid={`penal-cat-detail-${category}`}>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sheriff-gold hover:text-sheriff-gold-dark uppercase-label mb-6 transition-colors" data-testid="penal-back-btn">
        <ChevronLeft className="h-4 w-4" /> Torna ai Capitoli
      </button>

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-sheriff-gold mb-2">Capitolo</div>
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#F5E6B8] tracking-tighter">{category}</h2>
        <p className="text-[#B8A269] mt-2 font-mono text-sm">{items.length} articoli totali</p>
      </div>

      <div className="space-y-10">
        {subgroups.map(([sev, arr]) => {
          const s = SEVERITY[sev] || SEVERITY.INFRAZIONE;
          return (
            <div key={sev} data-testid={`penal-subgroup-${sev}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                <h3 className="font-heading font-bold text-lg text-[#F5E6B8]">{s.label}</h3>
                <span className="text-xs font-mono text-[#8B7B49]">· {arr.length}</span>
                <div className="flex-1 h-px bg-sheriff-gold/15" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {arr.map((p) => <ArticleCard key={p.code + p.title} item={p} onPick={onPick} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SearchResults({ results, onPick, q }) {
  if (results.length === 0) {
    return (
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-16 text-center" data-testid="penal-empty">
        <Sparkles className="h-10 w-10 text-sheriff-gold/40 mx-auto mb-4" />
        <p className="text-[#B8A269]">Nessun risultato per &quot;{q}&quot;</p>
      </div>
    );
  }
  return (
    <div data-testid="penal-search-results">
      <div className="mb-5 font-mono text-sm text-[#B8A269]">{results.length} risultat{results.length === 1 ? "o" : "i"} per &quot;{q}&quot;</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {results.map((p) => <ArticleCard key={p.code + p.title} item={p} onPick={onPick} showCat />)}
      </div>
    </div>
  );
}

function ArticleCard({ item, onPick, showCat }) {
  const s = SEVERITY[item.severity] || SEVERITY.INFRAZIONE;
  return (
    <button
      onClick={() => onPick(item)}
      data-testid={`penal-card-${item.code}`}
      className="group text-left bg-gradient-to-br from-[#143830] to-[#0F2620] hover:from-[#1B4D3E] hover:to-[#143830] border-2 border-sheriff-gold/40 hover:border-sheriff-gold shadow-lg shadow-sheriff-gold/5 rounded-2xl p-5 transition-all duration-200 hover:shadow-xl hover:shadow-sheriff-gold/10 hover:-translate-y-0.5 flex flex-col h-full"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-mono font-bold text-sheriff-gold text-sm truncate">{item.code}</span>
        <ChevronRight className="h-4 w-4 text-sheriff-gold/40 group-hover:text-sheriff-gold group-hover:translate-x-1 transition-all shrink-0" />
      </div>
      <h4 className="font-heading font-bold text-[#F5E6B8] group-hover:text-sheriff-gold transition-colors leading-snug mb-3 line-clamp-2">{item.title}</h4>
      <p className="text-[#8B7B49] text-xs leading-relaxed line-clamp-2 mb-3 flex-1">{item.description}</p>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] font-bold rounded-full border ${s.chip}`}>
          <span className={`h-1 w-1 rounded-full ${s.dot}`} />
          {s.label}
        </span>
        {item.process && <span className="text-[9px] uppercase tracking-[0.1em] font-bold rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/40 px-2 py-0.5">⚖ Processo</span>}
        {item.bail && <span data-testid={`penal-bail-badge-${item.code}`} className="text-[9px] uppercase tracking-[0.1em] font-bold rounded-full bg-emerald-500/15 text-emerald-300 border-emerald-500/40 border px-2 py-0.5">Cauzione</span>}
      </div>
      {showCat && <div className="text-[10px] text-[#8B7B49] uppercase tracking-[0.1em] mb-1">{item.category}</div>}
      <div className="flex items-center justify-between pt-2 border-t border-sheriff-gold/10 text-xs font-mono">
        <span className="text-[#D4C480]"><Lock className="h-3 w-3 inline mr-1" />{item.jail}</span>
        <span className="text-sheriff-gold font-bold">{item.fine}</span>
      </div>
    </button>
  );
}
